<!-- src/apps/secret/components/layout/BrandedFooter.vue -->

<script setup lang="ts">
  import FeedbackToggle from '@/shared/components/ui/FeedbackToggle.vue';
  import JurisdictionToggle from '@/shared/components/ui/JurisdictionToggle.vue';
  import LanguageToggle from '@/shared/components/ui/LanguageToggle.vue';
  import ThemeToggle from '@/shared/components/ui/ThemeToggle.vue';
  import { useFooterConfig } from '@/shared/composables/useFooterConfig';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import type { LayoutProps } from '@/types/ui/layouts';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';

const { t } = useI18n();

  const productIdentity = useProductIdentity();

  withDefaults(defineProps<LayoutProps>(), {});

  const bootstrapStore = useBootstrapStore();
  const {
    regions_enabled,
    regions,
    authentication,
    i18n_enabled,
    ot_version_long,
  } = storeToRefs(bootstrapStore);

  const { showVersionConfig } = useFooterConfig();

</script>
<template>
  <footer
    class="w-full min-w-[320px] border-t border-day-line bg-day-100 py-6 transition-colors duration-300 dark:border-white/[0.13] dark:bg-night"
    :aria-label="t('web.layout.site_footer')">
    <div
      v-if="productIdentity.isCanonical"
      class="container mx-auto max-w-2xl px-4">
      <div
        class="flex flex-col-reverse items-center justify-between space-y-6 space-y-reverse md:flex-row md:space-y-0">
        <div
          class="flex w-full flex-wrap items-center justify-center gap-4 text-center text-sm text-day-ink-muted md:w-auto md:justify-start md:text-left dark:text-night-ink-muted">
          <span
            v-if="displayVersion && showVersionConfig"
            :title="`${t('web.homepage.onetime_secret_literal')} ${t('web.COMMON.version')}`">
            v{{ ot_version_long }}
          </span>
        </div>

        <div
          v-if="displayToggles"
          class="flex items-center justify-center gap-4 md:w-auto md:justify-end">
          <JurisdictionToggle v-if="regions_enabled && regions" />

          <ThemeToggle
            class="text-day-ink-muted transition-colors duration-200 hover:text-day-ink dark:text-night-ink-muted dark:hover:text-night-ink"
            :aria-label="t('web.layout.toggle_dark_mode')" />
          <LanguageToggle
            v-if="i18n_enabled"
            :compact="true"
            max-height="max-h-dvh" />
          <FeedbackToggle
            v-if="displayFeedback && authentication?.enabled"
            class="text-day-ink-muted transition-colors duration-200 hover:text-day-ink dark:text-night-ink-muted dark:hover:text-night-ink"
            :aria-label="t('web.layout.provide_feedback')" />
        </div>
      </div>
    </div>
    <div
      v-else
      class="container mx-auto max-w-2xl px-4">
      <div class="flex flex-col items-center justify-center space-y-4">
        <!-- Theme Toggle -->
        <div
          v-if="displayToggles"
          class="flex w-full flex-nowrap items-center justify-center space-x-4 md:w-auto md:justify-end">
          <ThemeToggle
            class="text-day-ink-muted transition-colors duration-200 hover:text-day-ink dark:text-night-ink-muted dark:hover:text-night-ink"
            :aria-label="t('web.layout.toggle_dark_mode')" />
          <LanguageToggle
            v-if="i18n_enabled"
            :compact="true"
            max-height="max-h-dvh" />
        </div>
      </div>
    </div>
  </footer>
</template>
